/**
 * <s-sticky-buy data-target="#selector">
 * Slides a bottom buy bar into view once the target (the main product form) has scrolled
 * above the viewport, and slides it away again when the target is back on screen.
 * Keeps the bar's variant select and price in step with the main form's selected variant.
 */
class SStickyBuy extends HTMLElement {
  #ac;
  #observer;

  connectedCallback() {
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };
    const target = document.querySelector(this.dataset.target || '');
    this.#setVisible(false);
    if (!target) return;

    this.#observer = new IntersectionObserver(([entry]) => {
      const scrolledPast = !entry.isIntersecting && entry.boundingClientRect.bottom <= 0;
      if (scrolledPast) this.#syncFromMain(target);
      this.#setVisible(scrolledPast);
    });
    this.#observer.observe(target);

    const select = this.querySelector('[data-sticky-variant]');
    select?.addEventListener('change', () => this.#updatePrice(), opts);
    this.#updatePrice();
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#observer?.disconnect();
    document.body.style.paddingBottom = '';
  }

  #syncFromMain(target) {
    const mainId = target.querySelector('input[name="id"]')?.value;
    const select = this.querySelector('[data-sticky-variant]');
    if (!mainId || !(select instanceof HTMLSelectElement)) return;
    const option = select.querySelector(`option[value="${CSS.escape(mainId)}"]`);
    if (option && !option.disabled && select.value !== mainId) {
      select.value = mainId;
      this.#updatePrice();
    }
  }

  #updatePrice() {
    const select = this.querySelector('[data-sticky-variant]');
    const priceEl = this.querySelector('[data-sticky-price]');
    if (!(select instanceof HTMLSelectElement) || !priceEl) return;
    const price = select.selectedOptions[0]?.dataset.price;
    if (price) priceEl.textContent = price;
  }

  #setVisible(visible) {
    this.toggleAttribute('data-visible', visible);
    this.inert = !visible;
    this.setAttribute('aria-hidden', String(!visible));
    // Reserve room at the page bottom so the bar never covers the footer.
    document.body.style.paddingBottom = visible ? `${this.offsetHeight}px` : '';
  }
}

if (!customElements.get('s-sticky-buy')) customElements.define('s-sticky-buy', SStickyBuy);
